import { Box } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { HeroCarousel } from 'ui-kit'
import CmsBlock from '@src/components/cms/CmsBlock'
import DevicesLink from '@src/components/DevicesLink'
import Request from '@src/services/api/Request'
import { routePageHead } from '@src/utils/routeHead'
import { mapHomepageCarouselToHeroSlide } from '@src/utils/mapHomepageCarouselToHeroSlide'
import { IS_CAROUSEL_ENABLED } from '@src/settings'
import type { HomepageAttributes } from '@src/types/strapi'

/** Match epharmacy home hero side-arrow inset. */
const HOME_HERO_DESKTOP_GUTTER_REM = 12.5

export const Route = createFileRoute('/')({
  loader: async () => {
    const homepage = await Request.homepage()
    return {
      homepage: homepage?.attributes ?? null,
    }
  },
  staleTime: 60_000,
  head: ({ loaderData }) => routePageHead(loaderData?.homepage?.seo?.pageTitle),
  component: HomePage,
})

export function HomePageView({
  homepage,
}: {
  homepage: HomepageAttributes | null
}) {
  const slides = (homepage?.homepage_carousels?.data ?? [])
    .map((entry, index) =>
      entry.attributes
        ? mapHomepageCarouselToHeroSlide(entry.attributes, index)
        : null,
    )
    .filter((slide): slide is NonNullable<typeof slide> => Boolean(slide))

  return (
    <>
      {IS_CAROUSEL_ENABLED && slides.length > 0 && (
        <Box
          component="section"
          aria-label="Home hero carousel"
          sx={{ mb: '1.5rem' }}
        >
          <HeroCarousel
            slides={slides}
            desktopButtonsGutterRem={HOME_HERO_DESKTOP_GUTTER_REM}
            reserveMobileLogoSpace
            linkComponent={DevicesLink}
          />
        </Box>
      )}

      {homepage?.components?.map(({ __component, ...props }, index) => (
        <CmsBlock
          component={__component}
          key={`${__component}-${String(props.id ?? 'block')}-${index}`}
          {...props}
        />
      ))}
    </>
  )
}

function HomePage() {
  const { homepage } = Route.useLoaderData() as {
    homepage: HomepageAttributes | null
  }

  return <HomePageView homepage={homepage} />
}
